import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getApiBaseUrl } from '@/lib/api'
import { getAuthHeaders } from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'

const IMPERSONATION_KEY = 'admin_impersonation_email'

type AdminUser = {
	id: string
	email: string | null
	name: string | null
	signupDate?: string | null
	plans?: string[]
	totalFeedback?: number
	programs?: Array<{
		id: string
		name: string
		websiteUrl?: string | null
		/** Present when loaded from /api/admin/users */
		relation?: 'guest' | 'creator'
	}>
}

type SortKey = 'user' | 'program' | 'plans' | 'totalFeedback' | 'signupDate'
type SortDirection = 'asc' | 'desc'

export default function AdminUsersPage() {
	const [search, setSearch] = useState('')
	const [sortKey, setSortKey] = useState<SortKey>('totalFeedback')
	const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: ['admin-users'],
		queryFn: async () => {
			const response = await fetch(`${getApiBaseUrl()}/api/admin/users`, {
				headers: await getAuthHeaders(),
			})
			if (!response.ok) {
				let message = `Failed to load users (${response.status})`
				try {
					const body = await response.json()
					if (body?.error) message = String(body.error)
				} catch {
					/* ignore */
				}
				throw new Error(message)
			}
			return response.json()
		},
	})

	const impersonate = async (userId: string) => {
		const headers = await getAuthHeaders()
		if (typeof window !== 'undefined') {
			headers['X-App-Origin'] = window.location.origin
		}
		const response = await fetch(`${getApiBaseUrl()}/api/admin/impersonate`, {
			method: 'POST',
			headers,
			body: JSON.stringify({ userId }),
		})
		const payload = await response.json()
		if (!response.ok || !payload.redirectUrl) return
		if (payload.adminEmail) localStorage.setItem(IMPERSONATION_KEY, payload.adminEmail)
		window.location.assign(payload.redirectUrl)
	}

	const users = (data?.users || []) as AdminUser[]

	const formatSignupDate = (value?: string | null) => {
		if (!value) return '-'
		const date = new Date(value)
		if (Number.isNaN(date.getTime())) return '-'
		return new Intl.DateTimeFormat('fr-FR', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		}).format(date)
	}

	const toggleSort = (nextKey: SortKey) => {
		if (sortKey === nextKey) {
			setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
			return
		}
		setSortKey(nextKey)
		setSortDirection('asc')
	}

	const getProgramNames = (user: AdminUser) =>
		(user.programs || []).map((program) => program.name).join(', ')

	const getPlanLabels = (user: AdminUser) => (user.plans || []).join(', ')

	const filteredUsers = useMemo(() => {
		const query = search.trim().toLowerCase()
		if (!query) return users
		return users.filter((user) => {
			const email = (user.email || '').toLowerCase()
			const name = (user.name || '').toLowerCase()
			const programNames = getProgramNames(user).toLowerCase()
			return email.includes(query) || name.includes(query) || programNames.includes(query)
		})
	}, [users, search])

	const sortedUsers = useMemo(() => {
		const copy = [...filteredUsers]
		copy.sort((a, b) => {
			const direction = sortDirection === 'asc' ? 1 : -1
			if (sortKey === 'user') {
				const aValue = String(a.name || a.email || '').toLowerCase()
				const bValue = String(b.name || b.email || '').toLowerCase()
				return aValue.localeCompare(bValue) * direction
			}
			if (sortKey === 'program') {
				return getProgramNames(a).localeCompare(getProgramNames(b)) * direction
			}
			if (sortKey === 'plans') {
				return getPlanLabels(a).localeCompare(getPlanLabels(b)) * direction
			}
			if (sortKey === 'signupDate') {
				const aTime = a.signupDate ? new Date(a.signupDate).getTime() : 0
				const bTime = b.signupDate ? new Date(b.signupDate).getTime() : 0
				return (aTime - bTime) * direction
			}
			return ((a.totalFeedback || 0) - (b.totalFeedback || 0)) * direction
		})
		return copy
	}, [filteredUsers, sortDirection, sortKey])

	const sortIndicator = (key: SortKey) => {
		if (sortKey !== key) return ''
		return sortDirection === 'asc' ? ' ▲' : ' ▼'
	}

	return (
		<Card>
			<CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<CardTitle>Users</CardTitle>
				<Input
					type="search"
					placeholder="Search by email, name or program..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className="w-full sm:max-w-sm"
				/>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<div className="text-sm text-muted-foreground">Loading...</div>
				) : isError ? (
					<div className="space-y-2 text-sm">
						<p className="text-destructive">
							{error instanceof Error ? error.message : 'Unable to load users'}
						</p>
						<p className="text-muted-foreground">
							Check that the backend is running (e.g. <code className="rounded bg-muted px-1">bun dev</code>) and
							that you are signed in with the admin account.
						</p>
						<button
							type="button"
							onClick={() => refetch()}
							className="rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
						>
							Retry
						</button>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full text-sm">
							<thead>
								<tr className="border-b text-left">
									<th className="px-3 py-2">
										<button type="button" onClick={() => toggleSort('user')} className="font-semibold">
											User{sortIndicator('user')}
										</button>
									</th>
									<th className="px-3 py-2">
										<button type="button" onClick={() => toggleSort('program')} className="font-semibold">
											Program{sortIndicator('program')}
										</button>
									</th>
									<th className="px-3 py-2">
										<button type="button" onClick={() => toggleSort('plans')} className="font-semibold">
											Plan{sortIndicator('plans')}
										</button>
									</th>
									<th className="px-3 py-2">
										<button
											type="button"
											onClick={() => toggleSort('totalFeedback')}
											className="font-semibold"
										>
											Total feedback{sortIndicator('totalFeedback')}
										</button>
									</th>
									<th className="px-3 py-2">
										<button
											type="button"
											onClick={() => toggleSort('signupDate')}
											className="font-semibold"
										>
											Signup date{sortIndicator('signupDate')}
										</button>
									</th>
									<th className="px-3 py-2 text-right">Action</th>
								</tr>
							</thead>
							<tbody>
								{sortedUsers.length === 0 ? (
									<tr>
										<td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
											No users match your search
										</td>
									</tr>
								) : (
									sortedUsers.map((user) => (
										<tr key={user.id} className="border-b">
											<td className="px-3 py-2">
												<div className="font-medium">{user.name || user.email}</div>
												<div className="text-xs text-muted-foreground">{user.email}</div>
											</td>
											<td className="px-3 py-2">
												{user.programs?.length ? (
													<div className="flex flex-col gap-1">
														{user.programs.map((program) => {
															const label =
																program.relation === 'creator' ? 'creator' : 'guest'
															const title = (
																<>
																	{program.websiteUrl ? (
																		<a
																			href={program.websiteUrl}
																			target="_blank"
																			rel="noreferrer"
																			className="text-primary underline-offset-2 hover:underline"
																		>
																			{program.name}
																		</a>
																	) : (
																		<span>{program.name}</span>
																	)}
																	<span className="text-muted-foreground">
																		{' '}
																		- {label}
																	</span>
																</>
															)
															return (
																<div key={program.id} className="leading-snug">
																	{title}
																</div>
															)
														})}
													</div>
												) : (
													'-'
												)}
											</td>
											<td className="px-3 py-2">
												{user.plans?.length ? user.plans.join(', ') : '-'}
											</td>
											<td className="px-3 py-2">{user.totalFeedback ?? 0}</td>
											<td className="px-3 py-2">{formatSignupDate(user.signupDate)}</td>
											<td className="px-3 py-2 text-right">
												<button
													type="button"
													onClick={() => impersonate(user.id)}
													className="rounded bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"
												>
													View as {user.name || user.email}
												</button>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				)}
			</CardContent>
		</Card>
	)
}
